import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API_BASE_URL from "../../config/api";

interface UserDetail {
  id: string;
  email: string;
  name: string;
  avatar: string | null;
  isVerified: boolean;
  adminRole: string | null;
  verificationCode: string | null;
  // category: string;
  createdAt: string;
  updatedAt: string;
  _count: {
    chatSessions: number;
    messages: number;
    scripts: number;
    apiKeys: number;
  };
  subscription?: {
    planType: string;
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    billingCycle: string;
    cancelAtPeriodEnd: boolean;
    canceledAt?: string; // Add this line
  };
  chatSessions: Array<{
    id: string;
    title: string;
    createdAt: string;
    isActive: boolean;
    _count: {
      messages: number;
    };
  }>;
  messages: Array<{
    id: string;
    role: string;
    content: string;
    tokens: number | null;
    createdAt: string;
    conversation: {
      id: string;
      title: string | null;
      sessionId: string;
    };
  }>;
  scripts: Array<{
    id: string;
    name: string;
    websiteUrl: string;
    isActive: boolean;
    usageCount: number;
    createdAt: string;
  }>;
  apiKeys: Array<{
    id: string;
    keyName: string;
    keyPrefix: string;
    isActive: boolean;
    usageCount: number;
    lastUsedAt: string | null;
    createdAt: string;
    rateLimit?: number | null; // Add this line
    expiresAt?: string | null; // Add this line
  }>;
}
const truncateContent = (content: string, maxLength: number = 100) => {
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength) + "...";
};
const AdminUserDetail: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    email: "",
    adminRole: "",
    // category: "",
  });
  const [subscriptionEditMode, setSubscriptionEditMode] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState({
    status: "",
    planType: "",
    billingCycle: "",
    cancelAtPeriodEnd: false,
  });
  const [scriptEditMode, setScriptEditMode] = useState<string | null>(null);
  const [scriptToDelete, setScriptToDelete] = useState<string | null>(null);
  const [editingScript, setEditingScript] = useState({
    name: "",
    websiteUrl: "",
    isActive: true,
  });
  const [apiKeyEditMode, setApiKeyEditMode] = useState<string | null>(null);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<string | null>(null);
  const [apiKeyToRevoke, setApiKeyToRevoke] = useState<string | null>(null);
  const [editingApiKey, setEditingApiKey] = useState({
    keyName: "",
    isActive: true,
    rateLimit: "",
    expiresAt: "",
  });

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
    }
  }, [userId]);

  useEffect(() => {
    if (user?.subscription) {
      setSubscriptionData({
        status: user.subscription.status,
        planType: user.subscription.planType,
        billingCycle: user.subscription.billingCycle,
        cancelAtPeriodEnd: user.subscription.cancelAtPeriodEnd,
      });
    }
  }, [user]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.data);
        setEditData({
          name: data.data.name,
          email: data.data.email,
          adminRole: data.data.adminRole || "",
          // category: data.data.category || "",
        });
      } else {
        setError(data.error || "Failed to fetch user details");
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error("Fetch user details error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      // Update user role
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/role`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            role: editData.adminRole,
            name: editData.name,
            // category: editData.category, // Include category in the update
          }), // Include name in the update,
        }
      );

      const data = await response.json();

      if (data.success) {
        setEditMode(false);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to update user");
      }
    } catch (err) {
      setError("Failed to update user");
    }
  };

  const handleDeleteUser = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${API_BASE_URL}/admin/users/${userId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      console.log("deleted user data", data);

      if (data.success) {
        navigate("/admin/users"); // Redirect back to users list
      } else {
        setError(data.error || "Failed to delete user");
      }
    } catch (err) {
      setError("Failed to delete user");
    }
  };

  const handleUpdateSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/subscription/status`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: subscriptionData.status }),
        }
      );

      const data = await response.json();
      if (data.success) {
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to update subscription status");
      }
    } catch (err) {
      setError("Failed to update subscription status");
    }
  };

  const handleUpdateSubscription = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/users/${userId}/subscription`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            planType: subscriptionData.planType,
            billingCycle: subscriptionData.billingCycle,
            cancelAtPeriodEnd: subscriptionData.cancelAtPeriodEnd,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setSubscriptionEditMode(false);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to update subscription");
      }
    } catch (err) {
      setError("Failed to update subscription");
    }
  };
  const handleEditScript = (script: any) => {
    setEditingScript({
      name: script.name,
      websiteUrl: script.websiteUrl,
      isActive: script.isActive,
    });
    setScriptEditMode(script.id);
  };

  const handleUpdateScript = async (scriptId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/scripts/${scriptId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editingScript),
        }
      );

      const data = await response.json();
      if (data.success) {
        setScriptEditMode(null);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to update script");
      }
    } catch (err) {
      setError("Failed to update script");
    }
  };

  const handleDeleteScript = async (scriptId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/scripts/${scriptId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setScriptToDelete(null);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to delete script");
      }
    } catch (err) {
      setError("Failed to delete script");
    }
  };

  const handleEditApiKey = (apiKey: any) => {
    setEditingApiKey({
      keyName: apiKey.keyName,
      isActive: apiKey.isActive,
      rateLimit: apiKey.rateLimit?.toString() || "",
      expiresAt: apiKey.expiresAt
        ? new Date(apiKey.expiresAt).toISOString().split("T")[0]
        : "",
    });
    setApiKeyEditMode(apiKey.id);
  };

  const handleUpdateApiKey = async (apiKeyId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/api-keys/${apiKeyId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...editingApiKey,
            rateLimit: editingApiKey.rateLimit
              ? parseInt(editingApiKey.rateLimit)
              : null,
            expiresAt: editingApiKey.expiresAt || null,
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setApiKeyEditMode(null);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to update API key");
      }
    } catch (err) {
      setError("Failed to update API key");
    }
  };

  const handleRevokeApiKey = async (apiKeyId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/api-keys/${apiKeyId}/revoke`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setApiKeyToRevoke(null);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to revoke API key");
      }
    } catch (err) {
      setError("Failed to revoke API key");
    }
  };

  const handleDeleteApiKey = async (apiKeyId: string) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/api-keys/${apiKeyId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = await response.json();
      if (data.success) {
        setApiKeyToDelete(null);
        fetchUserDetails(); // Refresh data
        setError("");
      } else {
        setError(data.error || "Failed to delete API key");
      }
    } catch (err) {
      setError("Failed to delete API key");
    }
  };

  const getRoleBadgeColor = (role: string | null) => {
    switch (role) {
      case "SUPER_ADMIN":
        return "bg-red-100 text-red-800";
      case "USER":
        return "bg-blue-100 text-blue-800";
      case "USER_MANAGER":
        return "bg-blue-100 text-blue-800";
      case "FINANCE_MANAGER":
        return "bg-green-100 text-green-800";
      case "SUPPORT_MANAGER":
        return "bg-purple-100 text-purple-800";
      case "CONTENT_MANAGER":
        return "bg-yellow-100 text-yellow-800";
      case "AI_TRAINER":
        return "bg-indigo-100 text-indigo-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
        return "bg-gray-100 text-gray-800";
      case "canceled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };
  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case "user":
        return "bg-blue-100 text-blue-800";
      case "assistant":
        return "bg-green-100 text-green-800";
      case "system":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{error || "User not found"}</p>
            <button
              onClick={() => navigate("/admin/users")}
              className="mt-2 text-red-600 hover:text-red-800 underline"
            >
              Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate("/admin/users")}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  User Details
                </h1>
                <p className="text-gray-600">
                  {user.name} ({user.email})
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {!editMode && user.adminRole !== "SUPER_ADMIN" && (
                <>
                  <button
                    onClick={() => setEditMode(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Edit User
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(true)}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Delete User
                  </button>
                </>
              )}
              {editMode && (
                <>
                  <button
                    onClick={handleUpdateUser}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Save Changes
                  </button>
                  <button
                    onClick={() => setEditMode(false)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError("")}
              className="mt-2 text-red-600 hover:text-red-800 underline text-sm"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* User Profile Card */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center space-x-6">
              <div className="flex-shrink-0">
                {user.avatar ? (
                  <img
                    className="h-24 w-24 rounded-full"
                    src={user.avatar}
                    alt=""
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gray-300 flex items-center justify-center">
                    <svg
                      className="h-12 w-12 text-gray-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <div className="flex-1">
                {editMode ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Name
                      </label>
                      <input
                        type="text"
                        value={editData.name}
                        onChange={(e) =>
                          setEditData({ ...editData, name: e.target.value })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <input
                        type="email"
                        value={editData.email}
                        onChange={(e) =>
                          setEditData({ ...editData, email: e.target.value })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Role
                      </label>
                      <select
                        value={editData.adminRole}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            adminRole: e.target.value,
                          })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="USER">User</option>
                        <option value="USER_MANAGER">User Manager</option>
                        <option value="FINANCE_MANAGER">Finance Manager</option>
                        <option value="SUPPORT_MANAGER">Support Manager</option>
                        <option value="CONTENT_MANAGER">Content Manager</option>
                        <option value="AI_TRAINER">AI Trainer</option>
                      </select>
                    </div>
                    <div>
                      {/* <label className="block text-sm font-medium text-gray-700">
                        Category
                      </label> */}
                      {/* <select
                        value={editData.category}
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            category: e.target.value,
                          })
                        }
                        className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        <option value="ECOMMERCE">E-Commerce</option>
                        <option value="REAL_ESTATE">Real Estate</option>
                        <option value="ERP">ERP</option>
                        <option value="IP_REGISTRATION">IP Registration</option>
                      </select> */}
                    </div>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {user.name}
                    </h2>
                    <p className="text-gray-600">{user.email}</p>
                    <div className="mt-2 flex items-center space-x-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(
                          user.adminRole
                        )}`}
                      >
                        {user.adminRole || "User"}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.isVerified
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {user.isVerified ? "Verified" : "Pending Verification"}
                      </span>
                      <span className="text-sm text-gray-500">
                        Joined {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {user._count.chatSessions}
                </div>
                <div className="text-sm text-gray-600">Chat Sessions</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {user._count.messages}
                </div>
                <div className="text-sm text-gray-600">Messages</div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {user._count.scripts}
                </div>
                <div className="text-sm text-gray-600">Scripts</div>
              </div>
            </div>
          </div>

          {/* <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <div className="text-2xl font-bold text-gray-900">
                  {user._count.apiKeys}
                </div>
                <div className="text-sm text-gray-600">API Keys</div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Tabs */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {[
                { id: "overview", name: "Overview" },
                { id: "sessions", name: "Chat Sessions" },
                { id: "messages", name: "Messages" },
                { id: "scripts", name: "Scripts" },
                // { id: "apikeys", name: "API Keys" },
                { id: "subscription", name: "Subscription" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? "border-red-500 text-red-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    User Information
                  </h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Full Name
                      </dt>
                      <dd className="text-sm text-gray-900">{user.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Email Address
                      </dt>
                      <dd className="text-sm text-gray-900">{user.email}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Role
                      </dt>
                      <dd className="text-sm text-gray-900">
                        {user.adminRole || "Regular User"}
                      </dd>
                    </div>
                    <div>
          {/* <dt className="text-sm font-medium text-gray-500">
            Category
          </dt>
          <dd className="text-sm text-gray-900">
            {user.category ? user.category.replace('_', ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) : "Not specified"}
          </dd> */}
        </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Account Status
                      </dt>
                      <dd className="text-sm text-gray-900">
                        {user.isVerified ? "Verified" : "Pending Verification"}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Member Since
                      </dt>
                      <dd className="text-sm text-gray-900">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Last Updated
                      </dt>
                      <dd className="text-sm text-gray-900">
                        {new Date(user.updatedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Activity Summary
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Total Chat Sessions
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {user._count.chatSessions}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Total Messages
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {user._count.messages}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">
                        Created Scripts
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {user._count.scripts}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">API Keys</span>
                      <span className="text-sm font-medium text-gray-900">
                        {user._count.apiKeys}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "sessions" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Recent Chat Sessions
                </h3>
                {user.chatSessions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Messages
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {user.chatSessions.map((session) => (
                          <tr key={session.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {session.title || "Untitled Session"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {session._count.messages}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  session.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {session.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(session.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No chat sessions found.</p>
                )}
              </div>
            )}

            {activeTab === "messages" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Recent Messages
                </h3>
                {user.messages && user.messages.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Content
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Session
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tokens
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {user.messages.map((message) => (
                          <tr key={message.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                                  message.role
                                )}`}
                              >
                                {message.role.charAt(0).toUpperCase() +
                                  message.role.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                              <div className="truncate" title={message.content}>
                                {truncateContent(message.content, 80)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div
                                className="truncate max-w-xs"
                                title={
                                  message.conversation.title ||
                                  message.conversation.sessionId
                                }
                              >
                                {message.conversation.title ||
                                  `Session ${message.conversation.sessionId.slice(
                                    -8
                                  )}`}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {message.tokens || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(message.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No messages found.</p>
                )}
              </div>
            )}
            {activeTab === "scripts" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  User Scripts
                </h3>
                {user.scripts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Website
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Usage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {user.scripts.map((script) => (
                          <tr key={script.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {scriptEditMode === script.id ? (
                                <input
                                  type="text"
                                  value={editingScript.name}
                                  onChange={(e) =>
                                    setEditingScript({
                                      ...editingScript,
                                      name: e.target.value,
                                    })
                                  }
                                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                                />
                              ) : (
                                <span className="text-sm text-gray-900">
                                  {script.name}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {scriptEditMode === script.id ? (
                                <input
                                  type="url"
                                  value={editingScript.websiteUrl}
                                  onChange={(e) =>
                                    setEditingScript({
                                      ...editingScript,
                                      websiteUrl: e.target.value,
                                    })
                                  }
                                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                                />
                              ) : (
                                <span className="text-sm text-gray-500">
                                  {script.websiteUrl}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {script.usageCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {scriptEditMode === script.id ? (
                                <select
                                  value={editingScript.isActive.toString()}
                                  onChange={(e) =>
                                    setEditingScript({
                                      ...editingScript,
                                      isActive: e.target.value === "true",
                                    })
                                  }
                                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                                >
                                  <option value="true">Active</option>
                                  <option value="false">Inactive</option>
                                </select>
                              ) : (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    script.isActive
                                      ? "bg-green-100 text-green-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {script.isActive ? "Active" : "Inactive"}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(script.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {scriptEditMode === script.id ? (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() =>
                                      handleUpdateScript(script.id)
                                    }
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setScriptEditMode(null)}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditScript(script)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => setScriptToDelete(script.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No scripts found.</p>
                )}
              </div>
            )}

            {/* Delete Script Confirmation Modal */}
            {scriptToDelete && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                  <div className="mt-3 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mt-4">
                      Delete Script
                    </h3>
                    <div className="mt-2 px-7 py-3">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to delete this script? This action
                        cannot be undone.
                      </p>
                    </div>
                    <div className="items-center px-4 py-3 flex justify-center space-x-4">
                      <button
                        onClick={() => setScriptToDelete(null)}
                        className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteScript(scriptToDelete)}
                        className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700"
                      >
                        Delete Script
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* {activeTab === "scripts" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  User Scripts
                </h3>
                {user.scripts.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Website
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Usage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {user.scripts.map((script) => (
                          <tr key={script.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {script.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {script.websiteUrl}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {script.usageCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  script.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {script.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(script.createdAt).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No scripts found.</p>
                )}
              </div>
            )} */}
            {activeTab === "apikeys" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  API Keys
                </h3>
                {user.apiKeys.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Key Prefix
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Usage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Rate Limit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Used
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Expires
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {user.apiKeys.map((key) => (
                          <tr key={key.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {apiKeyEditMode === key.id ? (
                                <input
                                  type="text"
                                  value={editingApiKey.keyName}
                                  onChange={(e) =>
                                    setEditingApiKey({
                                      ...editingApiKey,
                                      keyName: e.target.value,
                                    })
                                  }
                                  className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                                />
                              ) : (
                                <span className="text-sm text-gray-900">
                                  {key.keyName}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                {key.keyPrefix}...
                              </code>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {key.usageCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {apiKeyEditMode === key.id ? (
                                <input
                                  type="number"
                                  value={editingApiKey.rateLimit}
                                  onChange={(e) =>
                                    setEditingApiKey({
                                      ...editingApiKey,
                                      rateLimit: e.target.value,
                                    })
                                  }
                                  placeholder="No limit"
                                  className="w-20 border border-gray-300 rounded-md px-2 py-1 text-sm"
                                />
                              ) : (
                                <span className="text-sm text-gray-500">
                                  {key.rateLimit || "No limit"}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {key.lastUsedAt
                                ? new Date(key.lastUsedAt).toLocaleDateString()
                                : "Never"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {apiKeyEditMode === key.id ? (
                                <select
                                  value={editingApiKey.isActive.toString()}
                                  onChange={(e) =>
                                    setEditingApiKey({
                                      ...editingApiKey,
                                      isActive: e.target.value === "true",
                                    })
                                  }
                                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                                >
                                  <option value="true">Active</option>
                                  <option value="false">Inactive</option>
                                </select>
                              ) : (
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    key.isActive
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {key.isActive ? "Active" : "Inactive"}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {apiKeyEditMode === key.id ? (
                                <input
                                  type="date"
                                  value={editingApiKey.expiresAt}
                                  onChange={(e) =>
                                    setEditingApiKey({
                                      ...editingApiKey,
                                      expiresAt: e.target.value,
                                    })
                                  }
                                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                                />
                              ) : (
                                <span className="text-sm text-gray-500">
                                  {key.expiresAt
                                    ? new Date(
                                        key.expiresAt
                                      ).toLocaleDateString()
                                    : "Never"}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              {apiKeyEditMode === key.id ? (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleUpdateApiKey(key.id)}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setApiKeyEditMode(null)}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditApiKey(key)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Edit
                                  </button>
                                  {key.isActive && (
                                    <button
                                      onClick={() => setApiKeyToRevoke(key.id)}
                                      className="text-orange-600 hover:text-orange-900"
                                    >
                                      Revoke
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setApiKeyToDelete(key.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No API keys found.</p>
                )}
              </div>
            )}

            {/* Revoke API Key Confirmation Modal */}
            {apiKeyToRevoke && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                  <div className="mt-3 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100">
                      <svg
                        className="h-6 w-6 text-orange-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mt-4">
                      Revoke API Key
                    </h3>
                    <div className="mt-2 px-7 py-3">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to revoke this API key? This will
                        immediately deactivate the key and stop all API access.
                      </p>
                    </div>
                    <div className="items-center px-4 py-3 flex justify-center space-x-4">
                      <button
                        onClick={() => setApiKeyToRevoke(null)}
                        className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleRevokeApiKey(apiKeyToRevoke)}
                        className="px-4 py-2 bg-orange-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-orange-700"
                      >
                        Revoke Key
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Delete API Key Confirmation Modal */}
            {apiKeyToDelete && (
              <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                  <div className="mt-3 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <svg
                        className="h-6 w-6 text-red-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mt-4">
                      Delete API Key
                    </h3>
                    <div className="mt-2 px-7 py-3">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to permanently delete this API
                        key? This action cannot be undone.
                      </p>
                    </div>
                    <div className="items-center px-4 py-3 flex justify-center space-x-4">
                      <button
                        onClick={() => setApiKeyToDelete(null)}
                        className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteApiKey(apiKeyToDelete)}
                        className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700"
                      >
                        Delete Key
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* {activeTab === "apikeys" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  API Keys
                </h3>
                {user.apiKeys.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Key Prefix
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Usage
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Used
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {user.apiKeys.map((key) => (
                          <tr key={key.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {key.keyName}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {key.keyPrefix}...
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {key.usageCount}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {key.lastUsedAt
                                ? new Date(key.lastUsedAt).toLocaleDateString()
                                : "Never"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  key.isActive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {key.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No API keys found.</p>
                )}
              </div>
            )} */}
            

            {activeTab === "subscription" && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Subscription Details
                </h3>
                {user.subscription ? (
                  <div className="bg-gray-50 rounded-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">
                          Plan Information
                        </h4>
                        <dl className="space-y-3">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Plan Type
                            </dt>
                            <dd className="text-sm text-gray-900 font-semibold">
                              {user.subscription.planType}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Billing Cycle
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {user.subscription.billingCycle}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Status
                            </dt>
                            <dd>
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(
                                  user.subscription.status
                                )}`}
                              >
                                {user.subscription.status}
                              </span>
                            </dd>
                          </div>
                        </dl>
                      </div>
                      <div>
                        <h4 className="text-md font-medium text-gray-900 mb-3">
                          Billing Information
                        </h4>
                        <dl className="space-y-3">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Current Period Start
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(
                                user.subscription.currentPeriodStart
                              ).toLocaleDateString()}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Current Period End
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {new Date(
                                user.subscription.currentPeriodEnd
                              ).toLocaleDateString()}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">
                              Cancel at Period End
                            </dt>
                            <dd className="text-sm text-gray-900">
                              {user.subscription.cancelAtPeriodEnd
                                ? "Yes"
                                : "No"}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No Subscription
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      This user does not have an active subscription.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">
                Delete User
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete <strong>{user.name}</strong>?
                  This action cannot be undone and will remove all their data
                  including:
                </p>
                <ul className="mt-2 text-sm text-gray-500 text-left">
                  <li>• {user._count.chatSessions} chat sessions</li>
                  <li>• {user._count.messages} messages</li>
                  <li>• {user._count.scripts} scripts</li>
                  <li>• {user._count.apiKeys} API keys</li>
                  {user.subscription && <li>• Subscription data</li>}
                </ul>
              </div>
              <div className="items-center px-4 py-3 flex justify-center space-x-4">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md shadow-sm hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUserDetail;
